import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  contractsApi,
  type ContractAuditEntry,
  type ContractUsage,
} from '@beacon/api-client';
import type { Contract } from '@beacon/core';
import type {
  ContractCreateInput,
  ContractUpdateInput,
} from '@beacon/core/validation';

import { beaconClient } from '@/lib/api/client.js';

export const contractKeys = {
  all: ['contracts'] as const,
  list: () => [...contractKeys.all, 'list'] as const,
  detail: (id: string) => [...contractKeys.all, 'detail', id] as const,
  usage: (id: string) => [...contractKeys.all, 'usage', id] as const,
  audit: (id: string) => [...contractKeys.all, 'audit', id] as const,
  metrics: (id: string) => [...contractKeys.all, 'metrics', id] as const,
};

export function useContracts() {
  return useQuery({
    queryKey: contractKeys.list(),
    queryFn: async (): Promise<Contract[]> => {
      const res = await contractsApi.list(beaconClient);
      return res.contracts;
    },
    staleTime: 5 * 60_000, // contracts change rarely; cache 5 min
  });
}

export function useContract(id: string | undefined) {
  return useQuery({
    queryKey: id ? contractKeys.detail(id) : contractKeys.all,
    queryFn: () => contractsApi.getById(beaconClient, id!),
    enabled: Boolean(id),
  });
}

export function useContractUsage(id: string | undefined) {
  return useQuery<ContractUsage>({
    queryKey: id ? contractKeys.usage(id) : contractKeys.all,
    queryFn: () => contractsApi.getUsage(beaconClient, id!),
    enabled: Boolean(id),
    staleTime: 60_000, // usage updates as time entries sync; minute is fine
  });
}

export function useContractAudit(id: string | undefined) {
  return useQuery<ContractAuditEntry[]>({
    queryKey: id ? contractKeys.audit(id) : contractKeys.all,
    queryFn: async () => {
      const res = await contractsApi.getAudit(beaconClient, id!);
      return res.entries;
    },
    enabled: Boolean(id),
    // Audit grows on every contract edit — invalidate on update mutation
    // covers staleness; meanwhile, a minute is fine for ambient browsing.
    staleTime: 60_000,
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useContractMetrics(id: string | undefined) {
  return useQuery<any>({
    queryKey: id ? contractKeys.metrics(id) : contractKeys.all,
    queryFn: () => beaconClient.get(`/api/contracts/${id}/metrics`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

/** Build a `contractId → Contract` index from the list — for join lookups. */
export function useContractsById() {
  const q = useContracts();
  if (!q.data) return new Map<string, Contract>();
  return new Map(q.data.map((c) => [c.id, c] as const));
}

export function useCreateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContractCreateInput) =>
      contractsApi.create(beaconClient, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contractKeys.list() });
    },
    onError: (err) => {
      toast.error("Couldn't create contract", {
        description: err instanceof Error ? err.message : String(err),
      });
    },
  });
}

interface UpdateVars {
  id: string;
  input: ContractUpdateInput;
}

export function useUpdateContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateVars) =>
      contractsApi.update(beaconClient, id, input),
    onSuccess: (server) => {
      qc.setQueryData(contractKeys.detail(server.id), server);
      qc.invalidateQueries({ queryKey: contractKeys.list() });
      qc.invalidateQueries({ queryKey: contractKeys.audit(server.id) });
    },
    onError: (err) => {
      toast.error("Couldn't update contract", {
        description: err instanceof Error ? err.message : String(err),
      });
    },
  });
}
